import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type { GridItemChangeEvent } from "@tql/ui-components-react/kendo/grid";
import type {
	TextAreaChangeEvent,
	TextBoxChangeEvent,
} from "@tql/ui-components-react/kendo/inputs";
import { TextArea, TextBox } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MaskedTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useEffect, useState } from "react";

import { LocationTypeOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/location-type-options/location-type-options.tsx";
import { ServiceOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/service-options/service-options.tsx";
import type { SearchResult } from "~/shared/components/fields/address-postal-code-search.tsx";
import { AddressPostalCodeSearch } from "~/shared/components/fields/address-postal-code-search.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import {
	formatTimeToString,
	parseTimeString,
} from "~/shared/utils/time-utils.ts";

import { useAddressAccessorialSelection } from "../../hooks/use-address-accessorial-selection.ts";
import type { CityStateZipFields } from "../city-state-zip-cell/city-state-zip-cell.tsx";
import styles from "./mobile-address-edit-modal.module.css";

type MaskedTextBoxChangeEvent = {
	value: string;
	syntheticEvent: React.SyntheticEvent;
};

type MobileAddressEditModalProps = {
	dataItem: AddressBookItemSummary;
	name: SubGroupKey;
	itemChange: (event: GridItemChangeEvent) => void;
	applyAddressFields: (
		dataItem: AddressBookItemSummary,
		fields: CityStateZipFields,
	) => void;
	onSave: (dataItem: AddressBookItemSummary) => void;
	onCancel: (dataItem: AddressBookItemSummary) => void;
};

type DraftTextFieldProps = {
	id: string;
	label: string;
	value: string;
	onCommit: (value: string) => void;
	maxLength?: number;
	inputMode?: "text" | "numeric";
};

// Mirrors the grid cells' local-state + commit-on-blur pattern so typing never
// dispatches an itemChange (and the resulting re-render) on every keystroke.
const DraftTextField = ({
	id,
	label,
	value,
	onCommit,
	maxLength,
	inputMode,
}: DraftTextFieldProps) => {
	const [localValue, setLocalValue] = useState(value);

	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	return (
		<div className={styles.field}>
			<label className={styles.label} htmlFor={id}>
				{label}
			</label>
			<TextBox
				id={id}
				inputMode={inputMode}
				maxLength={maxLength}
				onBlur={() => {
					if (localValue !== value) onCommit(localValue);
				}}
				onChange={(e: TextBoxChangeEvent) => {
					let next = e.value == null ? "" : String(e.value);
					if (inputMode === "numeric") {
						next = next.replaceAll(/\D/g, "");
					}
					setLocalValue(next);
				}}
				value={localValue}
			/>
		</div>
	);
};

export const MobileAddressEditModal = ({
	dataItem,
	name,
	itemChange,
	applyAddressFields,
	onSave,
	onCancel,
}: MobileAddressEditModalProps) => {
	const stopType = name === "pickup" ? 1 : 2;
	const detail = dataItem.details?.find(
		(addressDetail) => addressDetail.stopType === stopType,
	);
	const personLabel = name === "pickup" ? "Shipper" : "Receiver";

	const {
		locationTypeOptions,
		serviceOptions,
		isOptionSelected,
		handleRadioSelectionChange,
		handleCombinedSelectionChange,
	} = useAddressAccessorialSelection({ dataItem, name, itemChange });

	const [phoneDraft, setPhoneDraft] = useState(detail?.contactPhone ?? "");
	const [hoursOpenDraft, setHoursOpenDraft] = useState<Date | null>(
		parseTimeString(detail?.hoursOpen),
	);
	const [hoursCloseDraft, setHoursCloseDraft] = useState<Date | null>(
		parseTimeString(detail?.hoursClose),
	);
	const [specialInstructionsDraft, setSpecialInstructionsDraft] = useState(
		detail?.specialInstructions ?? "",
	);

	useEffect(() => {
		setPhoneDraft(detail?.contactPhone ?? "");
		setHoursOpenDraft(parseTimeString(detail?.hoursOpen));
		setHoursCloseDraft(parseTimeString(detail?.hoursClose));
		setSpecialInstructionsDraft(detail?.specialInstructions ?? "");
	}, [detail]);

	const commitField = (field: string, value: unknown) => {
		itemChange({ dataItem, field, value } as GridItemChangeEvent);
	};

	const handlePostalCodeSelection = (result: SearchResult | null) => {
		const zip = result?.type === "postalCode" ? result.data : null;
		if (!zip) return;
		applyAddressFields(dataItem, {
			city: zip.city.name,
			stateProvince: zip.city.state.abbreviation,
			postalCode: zip.postalCode,
			country: zip.city.state.countryCode,
		});
	};

	if (!detail) return null;

	const cityStateZip = [
		dataItem.city,
		[dataItem.stateProvince, dataItem.postalCode].filter(Boolean).join(" "),
	]
		.filter(Boolean)
		.join(", ");

	return (
		<TraxModal
			className={styles.modal}
			onClose={() => onCancel(dataItem)}
			title={"Edit Address"}
		>
			<div className={styles.fields}>
				<DraftTextField
					id={"mobile-address-company-name"}
					label={`${personLabel} Name`}
					onCommit={(value) => commitField("companyName", value)}
					value={dataItem.companyName ?? ""}
				/>
				<DraftTextField
					id={"mobile-address-address1"}
					label={"Address"}
					onCommit={(value) => commitField("address1", value)}
					value={dataItem.address1}
				/>
				<DraftTextField
					id={"mobile-address-address2"}
					label={"Address 2"}
					onCommit={(value) => commitField("address2", value)}
					value={dataItem.address2 ?? ""}
				/>
				<div className={styles.field}>
					<label
						className={styles.label}
						htmlFor={"mobile-address-city-state-zip"}
					>
						City, State, Zip
					</label>
					<AddressPostalCodeSearch
						id={"mobile-address-city-state-zip"}
						onSelectionChange={handlePostalCodeSelection}
						onlySearchPostalCodes={true}
						placeholder={"Postal Code"}
						value={cityStateZip}
					/>
				</div>
				<DraftTextField
					id={"mobile-address-contact-name"}
					label={"Contact"}
					onCommit={(value) => commitField("contactName", value)}
					value={detail.contactName ?? ""}
				/>
				<div className={styles.row}>
					<div className={styles.field}>
						<label className={styles.label} htmlFor={"mobile-address-phone"}>
							Phone
						</label>
						<MaskedTextBox
							id={"mobile-address-phone"}
							mask={"(000) 000-0000"}
							onBlur={() => {
								if (phoneDraft !== (detail.contactPhone ?? "")) {
									commitField("contactPhone", phoneDraft);
								}
							}}
							onChange={(e: MaskedTextBoxChangeEvent) => setPhoneDraft(e.value)}
							placeholder={"(___) ___-____"}
							value={phoneDraft}
						/>
					</div>
					<DraftTextField
						id={"mobile-address-ext"}
						inputMode={"numeric"}
						label={"Ext"}
						maxLength={10}
						onCommit={(value) => commitField("contactExtension", value)}
						value={detail.contactExtension ?? ""}
					/>
				</div>
				<div className={styles.field}>
					<span className={styles.label} id={"mobile-address-hours-label"}>
						{personLabel} Hours
					</span>
					<div className={styles.row}>
						<TimePicker
							aria-labelledby={"mobile-address-hours-label"}
							id={"mobile-address-hours-open"}
							onBlur={() => {
								const hoursOpen = formatTimeToString(hoursOpenDraft);
								const currentHoursOpen = formatTimeToString(
									parseTimeString(detail.hoursOpen),
								);
								if (hoursOpen !== currentHoursOpen) {
									commitField("hoursOpen", hoursOpen);
								}
							}}
							onChange={(e) => setHoursOpenDraft(e.value)}
							placeholder={"Open"}
							value={hoursOpenDraft}
						/>
						<TimePicker
							aria-labelledby={"mobile-address-hours-label"}
							id={"mobile-address-hours-close"}
							onBlur={() => {
								const hoursClose = formatTimeToString(hoursCloseDraft);
								const currentHoursClose = formatTimeToString(
									parseTimeString(detail.hoursClose),
								);
								if (hoursClose !== currentHoursClose) {
									commitField("hoursClose", hoursClose);
								}
							}}
							onChange={(e) => setHoursCloseDraft(e.value)}
							placeholder={"Close"}
							value={hoursCloseDraft}
						/>
					</div>
				</div>
				<div className={styles.field}>
					<span className={styles.sectionLabel}>Accessorials:</span>
					<LocationTypeOptions
						handleRadioSelectionChange={handleRadioSelectionChange}
						isOptionSelected={isOptionSelected}
						locationTypeOptions={locationTypeOptions}
						name={name}
					/>
					<ServiceOptions
						forceDesktopLayout={true}
						handleCombinedSelectionChange={handleCombinedSelectionChange}
						isOptionSelected={isOptionSelected}
						locationTypeOptions={locationTypeOptions}
						name={name}
						serviceOptions={serviceOptions}
					/>
				</div>
				<div className={styles.field}>
					<label
						className={styles.label}
						htmlFor={"mobile-address-special-instructions"}
					>
						Special Instructions:
					</label>
					<TextArea
						id={"mobile-address-special-instructions"}
						onBlur={() => {
							if (
								specialInstructionsDraft !== (detail.specialInstructions ?? "")
							) {
								commitField("specialInstructions", specialInstructionsDraft);
							}
						}}
						onChange={(e: TextAreaChangeEvent) => {
							setSpecialInstructionsDraft(e.value ?? "");
						}}
						rows={4}
						value={specialInstructionsDraft}
					/>
				</div>
			</div>
			<DialogActionsBar>
				<div className={styles.actions}>
					<Button onClick={() => onCancel(dataItem)}>Cancel</Button>
					<Button onClick={() => onSave(dataItem)} variant={"primary"}>
						Save
					</Button>
				</div>
			</DialogActionsBar>
		</TraxModal>
	);
};
